import { css } from '@emotion/react';
import type { CSSInterpolation } from '@emotion/serialize';
import type { ReactNode } from 'react';

import {
	HighlightText,
	InfoCircle,
	LineClamp,
	useCallout,
} from '@adflow/frontend/ui-common';
import { su, theme } from '@adflow/frontend/ui-theme';
import { useHighlightMeta } from '@adflow/frontend/util-common';

import { sharedCellContainerStyle } from './styles';

interface TruncatedCellProps {
	text?: string;
	value: string | null | undefined;
	align?: 'right' | 'left' | 'center';
	archived?: boolean;
	bold?: boolean;
	italics?: boolean;
	'data-testid'?: string;
	lines?: number;
	wordBreak?: boolean;
	selected?: boolean;
	stacked?: boolean;
	searchTerm?: string;
	doubleEllipsis?: boolean;
	textStyle?: CSSInterpolation;
	disableHighlight?: boolean;
	calloutText?: string;
	infoCircleText?: string;
	label?: string;
	moreItemsIndicator?: ReactNode | string;
}

const styles = {
	container: css(sharedCellContainerStyle, su.textSm, {
		display: 'flex',
		alignItems: 'center',
		width: '100%',
		overflow: 'hidden',
	}),

	rightAlign: css({
		justifyContent: 'flex-end',
		alignSelf: 'flex-end',
		fontWeight: '400',
		textAlign: 'right',
	}),
	centerAlign: css({ justifyContent: 'center', fontWeight: '400' }),
	archived: css({
		color: theme.colors.text.archivedRow,
		fontStyle: 'italic',
	}),
	// to overwrite archived style
	selected: css({
		color: theme.colors.text.primary,
	}),
};

export function TruncatedTextCell({
	align,
	value,
	archived = false,
	bold = false,
	italics = false,
	'data-testid': dataTestId,
	lines = 2,
	wordBreak,
	selected,
	searchTerm,
	stacked = false,
	doubleEllipsis = false,
	textStyle,
	disableHighlight,
	calloutText,
	infoCircleText,
	label,
	moreItemsIndicator,
	...rest
}: TruncatedCellProps) {
	const { highlightedText } = useHighlightMeta();

	const { callout, setShowViewCallout } = useCallout({
		text: calloutText ?? '',
	});

	return (
		<>
			{label ? (
				<label
					css={[
						su.textXs,
						{
							position: 'absolute',
							color: theme.colors.text.secondary,
							top: 0,
						},
					]}
				>
					{label}
				</label>
			) : null}
			<div
				css={[
					styles.container,
					align === 'right'
						? styles.rightAlign
						: align === 'center'
							? styles.centerAlign
							: null,
					archived && styles.archived,
					selected && styles.selected,
					bold && { fontWeight: '700' },
					italics && { fontStyle: 'italic' },
				]}
				data-testid={dataTestId}
				onMouseEnter={(e) => setShowViewCallout(e)}
				{...rest}
			>
				<LineClamp css={{ overflowWrap: 'anywhere' }} lines={lines}>
					<HighlightText
						css={textStyle}
						text={value === null || value === undefined ? '' : value}
						highlight={
							disableHighlight ? undefined : (searchTerm ?? highlightedText)
						}
						wordBreak={wordBreak}
						doubleEllipsis={doubleEllipsis}
						stacked={stacked}
					/>
				</LineClamp>
				{infoCircleText ? (
					<InfoCircle
						css={{ marginLeft: '0.25rem' }}
						calloutText={infoCircleText}
					/>
				) : null}
				{calloutText ? callout : null}
				{moreItemsIndicator ? (
					<span css={{ marginLeft: '0.5rem' }}>{moreItemsIndicator}</span>
				) : null}
			</div>
		</>
	);
}
